<template>
  <div>
    <v-container fluid>
      <!-- Header -->

      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{
            "Validation Test Runner"
          }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-toolbar flat color="transparent" class="my-2">
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="elevated" @click="openCreateDialog" prepend-icon="mdi-plus">
          New Test Job
        </v-btn>
      </v-toolbar>

      <!-- Current Queue Section -->
      <v-card class="pa-4 mb-4" elevation="2">
        <div class="text-subtitle-1 font-weight-bold mb-3">
          <v-icon class="mr-2">mdi-playlist-play</v-icon>
          Current Queue
        </div>

        <!-- Running Job -->
        <div v-if="runningJob" class="mb-3">
          <v-card variant="outlined" class="pa-3" color="info">
            <div class="d-flex align-center">
              <v-icon color="info" class="mr-2">mdi-play-circle</v-icon>
              <span class="font-weight-bold text-info">RUNNING:</span>
              <span class="ml-2">"{{ runningJob.jobName }}"</span>
              <v-spacer></v-spacer>
              <span class="text-body-2 mr-4">
                {{ runningJob.processedCount || 0 }}/{{ getTotalCalls(runningJob) }}
              </span>
              <v-chip size="small" color="info" variant="flat" class="mr-2">
                {{ getProgress(runningJob) }}%
              </v-chip>
              <v-btn size="small" color="error" variant="outlined" @click="cancelJob(runningJob)">
                Cancel
              </v-btn>
            </div>
            <v-progress-linear :model-value="getProgress(runningJob)" color="info" height="8" rounded
              class="mt-2"></v-progress-linear>
          </v-card>
        </div>

        <!-- Queued Jobs -->
        <div v-if="queuedJobs.length > 0">
          <div v-for="job in queuedJobs" :key="job.id" class="mb-2">
            <v-card variant="outlined" class="pa-3" color="warning">
              <div class="d-flex align-center">
                <v-icon color="warning" class="mr-2">mdi-clock-outline</v-icon>
                <span class="font-weight-bold text-warning">QUEUED:</span>
                <span class="ml-2">"{{ job.jobName }}"</span>
                <v-spacer></v-spacer>
                <span class="text-body-2 mr-4">{{ job.totalQuestions }} questions</span>
                <v-btn size="small" color="error" variant="text" @click="cancelJob(job)">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-card>
          </div>
        </div>

        <!-- Empty State -->
        <v-alert v-if="!runningJob && queuedJobs.length === 0" type="info" variant="tonal" density="compact">
          No jobs currently running or queued.
        </v-alert>
      </v-card>

      <!-- Main Content Card -->
      <v-card class="pa-4" elevation="2">
        <!-- Filter Bar -->
        <v-row class="mb-4" align="center">
          <v-col cols="12" md="2">
            <v-select v-model="filters.status" :items="statusFilterOptions" item-title="text" item-value="value"
              label="Status" variant="outlined" density="compact" clearable hide-details
              @update:model-value="loadJobs"></v-select>
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="filters.model" :items="modelFilterOptions" item-title="text" item-value="value"
              label="Model" variant="outlined" density="compact" clearable hide-details
              @update:model-value="loadJobs"></v-select>
          </v-col>
          <v-col cols="12" md="6" class="text-right">
            <v-btn variant="text" @click="clearFilters" prepend-icon="mdi-filter-off">
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>

        <!-- Jobs Table -->
        <v-data-table v-model:items-per-page="itemsPerPage" :headers="headers" :items="displayJobs"
          :items-per-page-options="[10, 25, 50]" :loading="false" class="elevation-0">
          <template v-slot:item.number="{ index }">
            <span class="text-body-2">{{ index + 1 }}</span>
          </template>

          <template v-slot:item.jobName="{ item }">
            <span class="text-body-2 font-weight-medium">{{ item.jobName }}</span>
          </template>

          <template v-slot:item.modelName="{ item }">
            <span class="text-body-2">{{ item.modelId }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip :color="getStatusColor(item.status)" size="small" variant="flat">
              {{ getStatusLabel(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.progress="{ item }">
            <div v-if="item.status === 'running'" class="d-flex align-center">
              <v-progress-linear :model-value="getProgress(item)" color="info" height="6" rounded style="width: 80px"
                class="mr-2"></v-progress-linear>
              <span class="text-body-2">{{ item.processedCount || 0 }}/{{ getTotalCalls(item) }}</span>
            </div>
            <span v-else class="text-body-2">
              {{ item.processedCount || 0 }}/{{ getTotalCalls(item) }}
            </span>
          </template>

          <!-- <template v-slot:item.results="{ item }">
            <span v-if="item.status === 'completed' && item.accuracy !== null" class="text-body-2">
              <v-icon size="small" color="success">mdi-check-circle</v-icon>
              {{ Math.round(item.totalQuestions * (item.accuracy / 100)) }} /
              <v-icon size="small" color="error">mdi-close-circle</v-icon>
              {{ item.totalQuestions - Math.round(item.totalQuestions * (item.accuracy / 100)) }}
            </span>
            <span v-else class="text-grey">-</span>
          </template> -->

          <template v-slot:item.duration="{ item }">
            <span class="text-body-2">{{ formatDuration(item) }}</span>
          </template>

          <!-- <template v-slot:item.accuracy="{ item }">
            <v-chip
              v-if="item.accuracy !== null"
              :color="getScoreColor(item.accuracy)"
              size="small"
              variant="tonal"
            >
              {{ Number(item.accuracy).toFixed(1) }}%
            </v-chip>
            <span v-else class="text-grey">-</span>
          </template> -->

          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center justify-end" style="white-space: nowrap;">
              <!-- View Details -->
              <v-tooltip text="View Details" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" size="small" color="primary" @click="viewJob(item)"
                    :disabled="item.status === 'pending'">
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>

              <!-- Edit (pending only - jobs can be edited before they start) -->
              <v-tooltip v-if="item.status === 'pending'" text="Edit Job" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" size="small" color="info" @click="editJob(item)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>

              <!-- Queue (pending only) -->
              <v-tooltip v-if="item.status === 'pending'" text="Queue Job" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" size="small" color="success" @click="queueJob(item)">
                    <v-icon>mdi-play</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>

              <!-- Cancel (running/queued only) -->
              <v-tooltip v-if="item.status === 'running' || item.status === 'queued'" text="Cancel Job" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" size="small" color="warning" @click="cancelJob(item)">
                    <v-icon>mdi-stop</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>

              <!-- Rerun (completed/failed/cancelled only) -->
              <v-tooltip v-if="!['running', 'queued', 'pending'].includes(item.status)" text="Rerun Job" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" size="small" color="info" @click="rerunJob(item)">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>

              <!-- Delete (not running) -->
              <v-tooltip v-if="item.status !== 'running'" text="Delete Job" location="top">
                <template v-slot:activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" size="small" color="error" @click="confirmDelete(item)">
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <!-- Create/Edit Test Job Dialog -->
    <v-dialog v-model="createDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-4 bg-primary">
          <v-icon class="mr-2">{{ isEditMode ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ isEditMode ? 'Edit Test Job' : 'Create New Test Job' }}
        </v-card-title>

        <v-card-text class="pa-4">
          <v-form ref="formRef" v-model="formValid">
            <!-- Job Name -->
            <div class="text-subtitle-2 mb-2 text-grey-darken-1">
              <v-icon class="mr-2" size="small">mdi-label</v-icon>
              Job Name *
            </div>
            <v-text-field v-model="form.jobName" variant="outlined" density="compact"
              placeholder="e.g., Weekly Full Test" :rules="[rules.required]" class="mb-4"></v-text-field>
            <!-- Configuration Section -->
            <div class="text-subtitle-2 mb-3 text-grey-darken-1">
              <v-icon class="mr-2" size="small">mdi-cog</v-icon>
              Configuration
            </div>

            <v-row>
              <v-col cols="12" md="6">
                <div class="text-caption mb-2">
                  Chatbot Model *
                  <v-tooltip location="top" max-width="300">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small" class="ml-1 cursor-pointer" color="grey-darken-1">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <div>
                      The chatbot model to be tested.<br />
                      This model will answer the validation questions.
                    </div>
                  </v-tooltip>
                </div>
                <v-select v-model="form.modelId" :items="models"
                  :item-title="(item) => `${item.modelDisplayID} - ${item.name || item.modelName || ''}`"
                  item-value="modelDisplayID" variant="outlined" density="compact" :rules="[rules.required]"
                  hide-details :loading="models.length === 0" placeholder="Select chatbot model"></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-caption mb-2">
                  Judge Model *
                  <v-tooltip location="top" max-width="300">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small" class="ml-1 cursor-pointer" color="grey-darken-1">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <div>
                      The LLM model used to judge answer quality.<br />
                      This model evaluates if the chatbot's answers are correct.<br />
                      <strong>Note:</strong> Cannot be the same as chatbot model to avoid bias.
                    </div>
                  </v-tooltip>
                </div>
                <v-select v-model="form.judgeModelId" :items="availableJudgeModels"
                  :item-title="(item) => `${item.modelDisplayID} - ${item.name || item.modelName || ''}`"
                  item-value="modelDisplayID" variant="outlined" density="compact" :rules="[rules.required]"
                  hide-details :loading="models.length === 0" placeholder="Select judge model"
                  :hint="form.modelId ? 'Chatbot model is excluded to prevent bias' : ''"
                  persistent-hint></v-select>
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <v-col cols="12" md="6">
                <div class="text-caption mb-2">Reliability (ask each question X times)</div>
                <v-select v-model="form.reliability" :items="[
                  { title: '1 (Single test)', value: 1 },
                  { title: '2 (Check consistency)', value: 2 },
                  { title: '3 (Check consistency)', value: 3 },
                  { title: '5 (High confidence)', value: 5 },
                  { title: '10 (Maximum)', value: 10 }
                ]" item-title="title" item-value="value" variant="outlined" density="compact"
                  hide-details></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-caption mb-2">Parallel Workers</div>
                <v-select v-model="form.parallelWorkers" :items="[
                  { title: '1 (Sequential)', value: 1 },
                  { title: '3 (Recommended)', value: 3 },
                  { title: '5 (Moderate)', value: 5 },
                  { title: '10 (High)', value: 10 },
                  { title: '15 (Very High)', value: 15 },
                  { title: '20 (Maximum)', value: 20 }
                ]" item-title="title" item-value="value" variant="outlined" density="compact"
                  hide-details></v-select>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Question Selection Section -->
            <div class="text-subtitle-2 mb-3 text-grey-darken-1">
              <v-icon class="mr-2" size="small">mdi-help-circle-outline</v-icon>
              Question Selection
            </div>

            <!-- Validation Type Radio Buttons -->
            <div class="mb-4">
              <v-radio-group v-model="validationType" hide-details color="primary" density="compact" inline>
                <v-radio value="answerOnly">
                  <template v-slot:label>
                    <div>
                      <span class="text-body-2 font-weight-medium">Questions without expected intents</span>
                      <div class="text-caption text-grey">Test only answer quality</div>
                    </div>
                  </template>
                </v-radio>
                <v-radio value="answerIntent" class="ml-4">
                  <template v-slot:label>
                    <div>
                      <span class="text-body-2 font-weight-medium">Questions with expected intents</span>
                      <div class="text-caption text-grey">Test both answer quality and intent routing</div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </div>

            <!-- Category and Question Selection Row -->
            <v-row>
              <!-- Category Select Box -->
              <v-col cols="12" md="6">
                <div class="text-caption mb-2">Category</div>
                <v-menu v-model="categoryMenuOpen" :close-on-content-click="false" location="bottom" offset="5">
                  <template v-slot:activator="{ props }">
                    <div
                      v-bind="props"
                      class="category-select-box d-flex align-center flex-wrap ga-1 pa-2 rounded border cursor-pointer"
                      :class="{ 'border-primary': categoryMenuOpen }"
                      style="min-height: 40px; border: 1px solid rgba(0,0,0,0.38);"
                    >
                      <template v-if="form.selectedCategoryIds.length > 0">
                        <v-chip
                          v-for="catId in form.selectedCategoryIds"
                          :key="catId"
                          size="small"
                          color="primary"
                          variant="tonal"
                          closable
                          @click:close.stop="removeCategory(catId)"
                        >
                          {{ truncateText(getCategoryNameById(catId), 50) }}
                        </v-chip>
                      </template>
                      <span v-else class="text-grey">Select categories</span>
                      <v-spacer></v-spacer>
                      <v-icon v-if="form.selectedCategoryIds.length > 0" size="small" class="mr-1" @click.stop="clearCategories">mdi-close</v-icon>
                      <v-icon size="small">mdi-chevron-down</v-icon>
                    </div>
                  </template>
                  <v-card min-width="300" max-height="400" class="overflow-y-auto">
                    <v-list density="compact">
                      <v-list-item @click="toggleAllCategories">
                        <template v-slot:prepend>
                          <v-checkbox-btn
                            :model-value="isAllCategoriesSelected"
                            :indeterminate="form.selectedCategoryIds.length > 0 && !isAllCategoriesSelected"
                            color="primary"
                          ></v-checkbox-btn>
                        </template>
                        <v-list-item-title class="font-weight-bold">All Categories</v-list-item-title>
                      </v-list-item>
                      <v-divider></v-divider>
                      <v-list-item
                        v-for="cat in filteredCategories"
                        :key="cat.id"
                        @click="toggleCategory(cat.id)"
                      >
                        <template v-slot:prepend>
                          <v-checkbox-btn
                            :model-value="form.selectedCategoryIds.includes(cat.id)"
                            color="primary"
                          ></v-checkbox-btn>
                        </template>
                        <v-list-item-title>{{ cat.categoryName }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </v-col>

              <!-- Question Select Box -->
              <v-col cols="12" md="6">
                <div class="text-caption mb-2">Questions</div>
                <v-menu v-model="questionMenuOpen" :close-on-content-click="false" location="bottom" offset="5">
                  <template v-slot:activator="{ props }">
                    <div
                      v-bind="props"
                      class="question-select-box d-flex align-center flex-wrap ga-1 pa-2 rounded border cursor-pointer"
                      :class="{ 'border-primary': questionMenuOpen }"
                      style="min-height: 40px; border: 1px solid rgba(0,0,0,0.38);"
                    >
                      <template v-if="form.selectedQuestionIds.length > 0">
                        <v-chip
                          v-for="qId in visibleQuestionChips"
                          :key="qId"
                          size="small"
                          color="primary"
                          variant="tonal"
                          closable
                          @click:close.stop="removeQuestion(qId)"
                        >
                          {{ getQuestionChipText(qId) }}
                        </v-chip>
                        <v-chip
                          v-if="form.selectedQuestionIds.length > 10"
                          size="small"
                          color="grey"
                          variant="tonal"
                        >
                          +{{ form.selectedQuestionIds.length - 10 }} more
                        </v-chip>
                      </template>
                      <span v-else class="text-grey">Select questions</span>
                      <v-spacer></v-spacer>
                      <v-icon v-if="form.selectedQuestionIds.length > 0" size="small" class="mr-1" @click.stop="clearQuestions">mdi-close</v-icon>
                      <v-icon size="small">mdi-chevron-down</v-icon>
                    </div>
                  </template>
                  <v-card min-width="400" max-height="400">
                    <v-text-field
                      v-model="questionSearchText"
                      placeholder="Search questions..."
                      prepend-inner-icon="mdi-magnify"
                      variant="solo-filled"
                      density="compact"
                      hide-details
                      class="ma-2"
                      clearable
                    ></v-text-field>
                    <v-divider></v-divider>
                    <v-list density="compact" class="overflow-y-auto" style="max-height: 300px;">
                      <v-list-item @click="toggleAllQuestions">
                        <template v-slot:prepend>
                          <v-checkbox-btn
                            :model-value="isAllQuestionsSelected"
                            :indeterminate="form.selectedQuestionIds.length > 0 && !isAllQuestionsSelected"
                            color="primary"
                          ></v-checkbox-btn>
                        </template>
                        <v-list-item-title class="font-weight-bold">All Questions ({{ displayableQuestions.length }})</v-list-item-title>
                      </v-list-item>
                      <v-divider></v-divider>
                      <v-list-item
                        v-for="q in searchedQuestions"
                        :key="q.id"
                        @click="toggleQuestion(q.id)"
                      >
                        <template v-slot:prepend>
                          <v-checkbox-btn
                            :model-value="form.selectedQuestionIds.includes(q.id)"
                            color="primary"
                          ></v-checkbox-btn>
                        </template>
                        <v-list-item-title>
                          {{ truncateText(q.question, 60) }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="searchedQuestions.length === 0">
                        <v-list-item-title class="text-center text-grey">No questions found</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </v-col>
            </v-row>

            <!-- Selected count summary -->
            <div class="text-caption text-grey mt-2">
              Selected: {{ form.selectedQuestionIds.length }} question(s)
              <span v-if="form.selectedCategoryIds.length > 0"> from {{ form.selectedCategoryIds.length }} category(ies)</span>
            </div>

            <v-divider class="my-4"></v-divider>

            <!-- Summary Section -->
            <v-alert type="info" variant="tonal" density="compact">
              <div class="d-flex justify-space-between">
                <span><strong>Questions:</strong> {{ calculatedQuestionCount }}</span>
                <!-- <span><strong>Estimated time:</strong> {{ estimatedTime }}</span> -->
              </div>
            </v-alert>
          </v-form>
        </v-card-text>
        <v-divider class="border-opacity-100" color="grey-lighten-2" thickness="2"></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="closeCreateDialog" :disabled="creatingAction !== null">Cancel</v-btn>
          <v-btn color="#4CAF50" variant="tonal"
            :disabled="!formValid || calculatedQuestionCount === 0 || creatingAction !== null"
            :loading="creatingAction === 'save'" @click="saveJob(false)" prepend-icon="mdi-content-save">
            {{ isEditMode ? 'Update' : 'Create' }}
          </v-btn>
          <v-btn color="primary" variant="elevated"
            :disabled="!formValid || calculatedQuestionCount === 0 || creatingAction !== null"
            :loading="creatingAction === 'queue'" @click="saveJob(true)" prepend-icon="mdi-play">
            {{ isEditMode ? 'Update & Queue' : 'Create & Queue' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Job Dialog -->
    <v-dialog v-model="viewDialog" max-width="750px">
      <v-card v-if="selectedJob">
        <v-card-title class="text-h6 pa-4 bg-secondary">
          <v-icon class="mr-2">mdi-information</v-icon>
          Job Details
          <v-chip v-if="selectedJob.runNumber > 1" color="info" variant="tonal" size="x-small" class="ml-2">
            Run #{{ selectedJob.runNumber }}
          </v-chip>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-table density="compact">
            <tbody>
              <tr>
                <td class="font-weight-bold" width="150">Job Name</td>
                <td>{{ selectedJob.jobName }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Job ID</td>
                <td class="text-caption">{{ selectedJob.jobId }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Status</td>
                <td>
                  <v-chip :color="getStatusColor(selectedJob.status)" size="small" variant="flat">
                    {{ getStatusLabel(selectedJob.status) }}
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">Model</td>
                <td>{{ selectedJob.modelId }}</td>
              </tr>
              <tr v-if="selectedJob.judgeModelId">
                <td class="font-weight-bold">Judge Model</td>
                <td>{{ selectedJob.judgeModelId }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Parallel Workers</td>
                <td>{{ selectedJob.parallelWorkers }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Questions</td>
                <td>{{ selectedJob.totalQuestions }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Reliability</td>
                <td>{{ selectedJob.reliability || 1 }}x ({{ getTotalCalls(selectedJob) }} API calls)</td>
              </tr>
              <tr v-if="selectedJob.accuracy != null || selectedJob.intentAccuracy != null || selectedJob.answerPassRate != null">
                <td class="font-weight-bold">Accuracy</td>
                <td>
                  <v-chip :color="getScoreColor(selectedJob.accuracy ?? selectedJob.intentAccuracy ?? selectedJob.answerPassRate ?? 0)" size="small" variant="tonal">
                    {{ (selectedJob.accuracy ?? selectedJob.intentAccuracy ?? selectedJob.answerPassRate ?? 0).toFixed(1) }}%
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">Created</td>
                <td>{{ formatDateTime(selectedJob.createdDate) }}</td>
              </tr>
              <tr v-if="selectedJob.startedAt">
                <td class="font-weight-bold">Started</td>
                <td>{{ formatDateTime(selectedJob.startedAt) }}</td>
              </tr>
              <tr v-if="selectedJob.completedAt">
                <td class="font-weight-bold">Completed</td>
                <td>{{ formatDateTime(selectedJob.completedAt) }}</td>
              </tr>
              <tr v-if="selectedJob.errorMessage">
                <td class="font-weight-bold">Error</td>
                <td class="text-error">{{ selectedJob.errorMessage }}</td>
              </tr>
            </tbody>
          </v-table>

          <!-- Run History Section -->
          <div v-if="jobRunHistory.length > 1" class="mt-4">
            <v-divider class="mb-4"></v-divider>
            <div class="text-subtitle-2 mb-3 d-flex align-center">
              <v-icon class="mr-2" size="small">mdi-history</v-icon>
              Run History
              <v-chip size="x-small" color="grey" variant="tonal" class="ml-2">
                {{ jobRunHistory.length }} runs
              </v-chip>
            </div>

            <v-table density="compact" class="run-history-table">
              <thead>
                <tr>
                  <th width="60">Run</th>
                  <th>Date</th>
                  <th width="100">Status</th>
                  <!-- <th width="90">Score</th>
                  <th width="100">Pass/Fail</th> -->
                  <th width="80">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="run in jobRunHistory" :key="run.id" :class="{ 'current-run': run.id === selectedJob.id }">
                  <td>
                    <v-chip size="x-small" :color="run.id === selectedJob.id ? 'primary' : 'grey'" variant="flat">
                      #{{ run.runNumber }}
                    </v-chip>
                  </td>
                  <td class="text-caption">{{ formatDateShort(run.createdDate) }}</td>
                  <td>
                    <v-chip :color="getStatusColor(run.status)" size="x-small" variant="flat">
                      {{ getStatusLabel(run.status) }}
                    </v-chip>
                  </td>
                  <!-- <td>
                    <span v-if="run.accuracy !== null" :class="getScoreClass(run.accuracy)">
                      {{ Number(run.accuracy).toFixed(1) }}%
                    </span>
                    <span v-else class="text-grey">-</span>
                  </td>
                  <td>
                    <span v-if="run.status === 'completed' && run.accuracy !== null" class="text-caption">
                      <span class="text-success">{{ getPassCount(run) }}</span>
                      /
                      <span class="text-error">{{ getFailCount(run) }}</span>
                    </span>
                    <span v-else class="text-grey">-</span>
                  </td> -->
                  <td class="text-caption">{{ formatDuration(run) }}</td>
                </tr>
              </tbody>
            </v-table>

            <!-- Trend Indicator -->
            <!-- <div v-if="scoreTrend !== null" class="mt-3 d-flex align-center">
              <v-icon :color="scoreTrend > 0 ? 'success' : scoreTrend < 0 ? 'error' : 'grey'" size="small" class="mr-1">
                {{ scoreTrend > 0 ? 'mdi-trending-up' : scoreTrend < 0 ? 'mdi-trending-down' : 'mdi-minus' }} </v-icon>
                  <span class="text-caption"
                    :class="scoreTrend > 0 ? 'text-success' : scoreTrend < 0 ? 'text-error' : 'text-grey'">
                    {{ scoreTrend > 0 ? '+' : '' }}{{ scoreTrend.toFixed(1) }}% from first run
                  </span>
            </div> -->
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-btn v-if="selectedJob && !['running', 'queued', 'pending'].includes(selectedJob.status)" color="info"
            variant="tonal" @click="rerunFromDialog" prepend-icon="mdi-refresh">
            Rerun Job
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="viewDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const jobStatuses = {
  pending: { label: 'Pending', color: 'grey' },
  queued: { label: 'Queued', color: 'warning' },
  running: { label: 'Running', color: 'info' },
  completed: { label: 'Completed', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

export default {
  name: 'ValidationTestRunnerV2',
  inject: ["flagNav", "FontColorTitle", "SubNavbarBackGround"],
  data() {
    return {
      today: null,
      // Table data
      jobs: [],
      totalJobs: 0,
      itemsPerPage: 10,
      loading: false,
      creatingAction: null, // 'create' or 'queue' or null

      // Filters
      filters: {
        status: null,
        model: null,
      },

      // Reference data
      models: [],
      categories: [],
      allQuestions: [],
      loadingQuestions: false,
      questionFilter: 'all', // Filter for question types: 'all', 'answerOnly', 'both'
      
      // New question selection controls
      validationType: 'answerOnly', // 'answerOnly' or 'answerIntent'
      categoryMenuOpen: false,
      questionMenuOpen: false,
      questionSearchText: '',

      // Create/Edit dialog
      createDialog: false,
      formValid: false,
      editingJob: null, // Job being edited (null = create mode)
      form: {
        jobName: '',
        modelId: null,
        judgeModelId: null, // V2: Judge model for answer quality evaluation
        reliability: 1,
        parallelWorkers: 3,
        selectionType: 'all',
        selectedCategoryIds: [],  // Now stores category IDs instead of names
        selectedQuestionIds: [],
        limitPerCategory: null,
      },

      // View dialog
      viewDialog: false,
      selectedJob: null,
      jobRunHistory: [],

      // Table headers
      headers: [
        { title: '#', key: 'number', width: '60px', sortable: false },
        { title: 'Job Name', key: 'jobName' },
        { title: 'Model', key: 'modelName', width: '120px' },
        { title: 'Status', key: 'status', width: '120px' },
        { title: 'Progress', key: 'progress', width: '150px' },
        // { title: 'Results', key: 'results', width: '120px' },
        { title: 'Duration', key: 'duration', width: '100px' },
        // { title: 'Score', key: 'accuracy', width: '100px' },
        { title: 'Actions', key: 'actions', width: '180px', sortable: false, align: 'center' },
      ],

      // Validation rules
      rules: {
        required: (v) => !!v || 'This field is required',
      },

      // Queue polling interval
      queuePollInterval: null,
    };
  },

  computed: {
    runningJob() {
      return this.jobs.find((job) => job.status === 'running');
    },

    queuedJobs() {
      return this.jobs.filter((job) => job.status === 'queued');
    },

    statusFilterOptions() {
      return [
        { text: 'All', value: null },
        { text: 'Pending', value: 'pending' },
        { text: 'Queued', value: 'queued' },
        { text: 'Running', value: 'running' },
        { text: 'Completed', value: 'completed' },
        { text: 'Failed', value: 'failed' },
        { text: 'Cancelled', value: 'cancelled' },
      ];
    },

    modelFilterOptions() {
      return [
        { text: 'All', value: null },
        ...this.models.map((m) => ({
          text: `${m.modelDisplayID} - ${m.name || m.modelName || ''}`,
          value: m.modelDisplayID,
        })),
      ];
    },

    isEditMode() {
      return this.editingJob !== null;
    },

    totalQuestions() {
      return this.categories.reduce((sum, cat) => sum + (cat.questionCount || 0), 0);
    },

    calculatedQuestionCount() {
      // Now always based on selected question IDs
      return this.form.selectedQuestionIds?.length || 0;
    },

    estimatedTime() {
      const questions = this.calculatedQuestionCount;
      const workers = this.form.parallelWorkers || 1;
      const avgTimePerQuestion = 3; // seconds
      const totalSeconds = (questions * avgTimePerQuestion) / workers;

      if (totalSeconds < 60) return `~${Math.ceil(totalSeconds)} sec`;
      const minutes = Math.ceil(totalSeconds / 60);
      if (minutes < 60) return `~${minutes} min`;
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `~${hours}h ${remainingMinutes}m`;
    },

    // Filter questions by validation type
    filteredQuestions() {
      if (this.questionFilter === 'all') {
        return this.allQuestions;
      }
      if (this.questionFilter === 'answerOnly') {
        // Answer validation only (ValidateIntent = false, ValidateAnswer = true)
        return this.allQuestions.filter(q => !q.validateIntent && q.validateAnswer);
      }
      if (this.questionFilter === 'both') {
        // Both validations (ValidateIntent = true, ValidateAnswer = true)
        return this.allQuestions.filter(q => q.validateIntent && q.validateAnswer);
      }
      return this.allQuestions;
    },

    // Count questions with answer validation only
    answerOnlyCount() {
      return this.allQuestions.filter(q => !q.validateIntent && q.validateAnswer).length;
    },

    // Count questions with both validations
    bothCount() {
      return this.allQuestions.filter(q => q.validateIntent && q.validateAnswer).length;
    },

    // Questions filtered by validation type and category
    displayableQuestions() {
      // Filter by validateIntent flag based on radio selection
      // 'answerOnly': Show questions with validateIntent = false
      // 'answerIntent': Show questions with validateIntent = true
      let questions = this.allQuestions.filter(q => 
        this.validationType === 'answerIntent' ? q.validateIntent === true : q.validateIntent === false
      );
      
      // Don't filter by category here - show all questions matching validation type
      // Category selection is just for convenience, not for limiting what can be selected
      return questions;
    },

    // Questions filtered by search text
    searchedQuestions() {
      if (!this.questionSearchText) {
        return this.displayableQuestions;
      }
      const search = this.questionSearchText.toLowerCase();
      return this.displayableQuestions.filter(q => 
        q.question?.toLowerCase().includes(search) || 
        q.id?.toString().includes(search) ||
        q.category?.toLowerCase().includes(search)
      );
    },

    // Categories filtered to only show those with questions
    filteredCategories() {
      return this.categories.filter(cat => cat.questionCount > 0);
    },

    // Check if all categories are selected
    isAllCategoriesSelected() {
      return this.filteredCategories.length > 0 && this.form.selectedCategoryIds.length === this.filteredCategories.length;
    },

    // Check if all displayable questions are selected
    isAllQuestionsSelected() {
      const displayableIds = this.displayableQuestions.map(q => q.id);
      return displayableIds.length > 0 && displayableIds.every(id => this.form.selectedQuestionIds.includes(id));
    },

    // Display text for category select box
    categoryDisplayText() {
      if (this.form.selectedCategoryIds.length === 0) return '';
      if (this.isAllCategoriesSelected) return 'All Categories';
      return `${this.form.selectedCategoryIds.length} selected`;
    },

    // Display text for question select box
    questionDisplayText() {
      if (this.form.selectedQuestionIds.length === 0) return '';
      if (this.isAllQuestionsSelected) return `All Questions (${this.form.selectedQuestionIds.length})`;
      return `${this.form.selectedQuestionIds.length} selected`;
    },

    // Visible question chips (max 10)
    visibleQuestionChips() {
      return this.form.selectedQuestionIds.slice(0, 10);
    },

    displayJobs() {
      const groups = {};
      this.jobs.forEach(job => {
        const rootId = job.linkedJobId || job.id;
        if (!groups[rootId] || job.runNumber > (groups[rootId].runNumber || 1)) {
          groups[rootId] = job;
        }
      });
      return Object.values(groups);
    },

    scoreTrend() {
      const getAccuracy = (run) => run.accuracy ?? run.intentAccuracy ?? run.answerPassRate;
      const completedRuns = this.jobRunHistory.filter(
        (run) => run.status === 'completed' && getAccuracy(run) != null
      );

      if (completedRuns.length < 2) return null;

      const firstRun = completedRuns[0];
      const lastRun = completedRuns[completedRuns.length - 1];

      return (getAccuracy(lastRun) ?? 0) - (getAccuracy(firstRun) ?? 0);
    },

    // Available judge models (excludes selected chatbot model to prevent bias)
    availableJudgeModels() {
      if (!this.form.modelId) {
        return this.models;
      }
      // Filter out the selected chatbot model from judge model options
      return this.models.filter(m => m.modelDisplayID !== this.form.modelId);
    },
  },

  watch: {
    // When validation type changes, filter selected questions to only include valid ones
    validationType() {
      const validIds = this.displayableQuestions.map(q => q.id);
      this.form.selectedQuestionIds = this.form.selectedQuestionIds.filter(id => validIds.includes(id));
    },

    // When chatbot model changes, clear judge model if it's the same (prevent bias)
    'form.modelId'(newModelId) {
      if (newModelId && this.form.judgeModelId === newModelId) {
        this.form.judgeModelId = null;
        this.$swal.fire({
          icon: 'info',
          title: 'Judge Model Cleared',
          text: 'Judge model cannot be the same as chatbot model to avoid self-evaluation bias. Please select a different judge model.',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    },
  },

  mounted() {
    this.GetDate();
    this.loadJobs();
    this.loadModels();
    this.loadCategories();
    this.startQueuePolling();
  },

  beforeUnmount() {
    this.stopQueuePolling();
  },

  methods: {
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1; //January is 0!

      let yyyy = today1.getFullYear();
      if (dd < 10) {
          dd = "0" + dd;
      }
      if (mm < 10) {
          mm = "0" + mm;
      }

      this.today = dd + "/" + mm + "/" + yyyy;
    },
    // Data Loading
    loadJobs() {
      this.loading = true;
      const request = {
        Page: 1,
        PerPage: 100,
        Status: this.filters.status,
        ModelId: this.filters.model,
      };

      this.$call.GetTableValidationJobs(
        request,
        (response) => {
          this.loading = false;
          if (response.status === 0 && response.data) {
            this.jobs = response.data.data || [];
            this.totalJobs = response.data.total || 0;
          }
        },
        (error) => {
          this.loading = false;
          console.error('Failed to load jobs:', error);
        }
      );
    },

    loadModels() {
      // Use DDGPTModelIsPublish which returns proper INT id for the model
      this.$call.DDGPTModelIsPublish(
        (response) => {
          if (response.status === 0 && response.data) {
            this.models = response.data;
            if (this.models.length > 0 && this.form.modelId === null) {
              this.form.modelId = this.models[0].modelDisplayID;
            }
          }
        },
        (error) => {
          console.error('Failed to load models:', error);
        }
      );
    },

    loadCategories() {
      this.$call.GetAllIntentCategories(
        (response) => {
          if (response.status === 0 && response.data) {
            this.categories = response.data;
          }
        },
        (error) => {
          console.error('Failed to load categories:', error);
        }
      );
    },

    loadJobRunHistory(jobId) {
      this.$call.GetValidationJobRunHistory(
        jobId,
        (response) => {
          if (response.status === 0 && response.data) {
            this.jobRunHistory = response.data;
          }
        },
        (error) => {
          console.error('Failed to load job run history:', error);
          this.jobRunHistory = [];
        }
      );
    },

    // Queue Polling
    startQueuePolling() {
      this.queuePollInterval = setInterval(() => {
        if (this.runningJob || this.queuedJobs.length > 0) {
          this.loadJobs();
        }
      }, 5000);
    },

    stopQueuePolling() {
      if (this.queuePollInterval) {
        clearInterval(this.queuePollInterval);
        this.queuePollInterval = null;
      }
    },

    // Helper Methods
    getProgress(job) {
      if (!job || job.totalQuestions === 0) return 0;
      const totalCalls = job.totalQuestions * (job.reliability || 1);
      return Math.min(100, Math.round(((job.processedCount || 0) / totalCalls) * 100));
    },

    getTotalCalls(job) {
      if (!job) return 0;
      return job.totalQuestions * (job.reliability || 1);
    },

    getStatusColor(status) {
      return jobStatuses[status]?.color || 'grey';
    },

    getStatusLabel(status) {
      return jobStatuses[status]?.label || status;
    },

    getScoreColor(score) {
      if (score >= 95) return 'success';
      if (score >= 85) return 'info';
      if (score >= 70) return 'warning';
      return 'error';
    },

    formatDuration(job) {
      if (!job.startedAt || !job.completedAt) return '-';
      const endTime = new Date(job.completedAt);
      const startTime = new Date(job.startedAt);
      const totalSeconds = Math.round((endTime - startTime) / 1000);
      if (totalSeconds < 60) return `${totalSeconds}s`;
      const minutes = Math.round(totalSeconds / 60);
      if (minutes < 60) return `${minutes}m`;
      return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString();
    },

    formatDateShort(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    getPassCount(job) {
      const accuracy = job.accuracy ?? job.intentAccuracy ?? job.answerPassRate ?? 0;
      if (accuracy == null || job.totalQuestions === 0) return 0;
      return Math.round(job.totalQuestions * (accuracy / 100));
    },

    getFailCount(job) {
      const accuracy = job.accuracy ?? job.intentAccuracy ?? job.answerPassRate ?? 0;
      if (accuracy == null || job.totalQuestions === 0) return 0;
      return job.totalQuestions - this.getPassCount(job);
    },

    getScoreClass(score) {
      if (score >= 95) return 'text-success font-weight-bold';
      if (score >= 85) return 'text-info';
      if (score >= 70) return 'text-warning';
      return 'text-error';
    },

    clearFilters() {
      this.filters = {
        status: null,
        model: null,
      };
      this.loadJobs();
    },

    // Dialog Methods
    openCreateDialog() {
      this.resetForm();
      this.loadAllQuestions(); // Pre-load questions for manual selection
      this.createDialog = true;
    },

    closeCreateDialog() {
      this.createDialog = false;
      this.editingJob = null;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        jobName: '',
        modelId: this.models.length > 0 ? this.models[0].modelDisplayID : null,
        judgeModelId: this.models.length > 1 ? this.models[1].modelDisplayID : null,  // ← Use SECOND model to avoid same selection
        reliability: 1,
        parallelWorkers: 3,
        selectionType: 'manual', // Always manual now
        selectedCategoryIds: [],  // Now stores category IDs
        selectedQuestionIds: [],
        limitPerCategory: null,
      };
      // Reset new selection controls
      this.validationType = 'answerOnly';
      this.categoryMenuOpen = false;
      this.questionMenuOpen = false;
      this.questionSearchText = '';
      if (this.$refs.formRef) {
        this.$refs.formRef.resetValidation();
      }
    },

    // Category Selection Methods
    toggleAllCategories() {
      if (this.isAllCategoriesSelected) {
        this.form.selectedCategoryIds = [];
        // Clear all questions when all categories are deselected
        this.form.selectedQuestionIds = [];
      } else {
        this.form.selectedCategoryIds = this.filteredCategories.map(c => c.id);
        // Select all questions from all categories
        this.form.selectedQuestionIds = this.displayableQuestions.map(q => q.id);
      }
    },

    toggleCategory(categoryId) {
      const index = this.form.selectedCategoryIds.indexOf(categoryId);
      if (index > -1) {
        // Deselecting category - remove questions from this category
        this.form.selectedCategoryIds.splice(index, 1);
        this.removeQuestionsFromCategory(categoryId);
      } else {
        // Selecting category - add questions from this category
        this.form.selectedCategoryIds.push(categoryId);
        this.addQuestionsFromCategory(categoryId);
      }
    },

    removeCategory(categoryId) {
      const index = this.form.selectedCategoryIds.indexOf(categoryId);
      if (index > -1) {
        this.form.selectedCategoryIds.splice(index, 1);
        // Remove questions from this category
        this.removeQuestionsFromCategory(categoryId);
      }
    },

    clearCategories() {
      this.form.selectedCategoryIds = [];
      // Clear question selection as well since categories are cleared
      this.form.selectedQuestionIds = [];
    },

    addQuestionsFromCategory(categoryId) {
      // Get category name from ID
      const category = this.categories.find(c => c.id === categoryId);
      if (!category) return;
      const categoryName = category.categoryName;
      
      // Get all questions from this category that match the current validation type
      const categoryQuestions = this.allQuestions.filter(q => 
        q.category === categoryName && 
        (this.validationType === 'answerIntent' ? q.validateIntent === true : q.validateIntent === false)
      );
      // Add their IDs to selection (avoid duplicates)
      const currentIds = new Set(this.form.selectedQuestionIds);
      categoryQuestions.forEach(q => currentIds.add(q.id));
      this.form.selectedQuestionIds = Array.from(currentIds);
    },

    removeQuestionsFromCategory(categoryId) {
      // Get category name from ID
      const category = this.categories.find(c => c.id === categoryId);
      if (!category) return;
      const categoryName = category.categoryName;
      
      // Get all question IDs from this category
      const categoryQuestionIds = this.allQuestions
        .filter(q => q.category === categoryName)
        .map(q => q.id);
      // Remove them from selection
      this.form.selectedQuestionIds = this.form.selectedQuestionIds.filter(
        id => !categoryQuestionIds.includes(id)
      );
    },

    updateQuestionsForCategories() {
      // When categories change, filter selected questions to only include those in selected categories
      if (this.form.selectedCategoryIds.length > 0) {
        const validQuestionIds = this.displayableQuestions.map(q => q.id);
        this.form.selectedQuestionIds = this.form.selectedQuestionIds.filter(id => validQuestionIds.includes(id));
      }
    },

    // Helper to get category name by ID
    getCategoryNameById(categoryId) {
      const category = this.categories.find(c => c.id === categoryId);
      return category ? category.categoryName : `Category #${categoryId}`;
    },

    // Helper to get category ID by name
    getCategoryIdByName(categoryName) {
      const category = this.categories.find(c => c.categoryName === categoryName);
      return category ? category.id : null;
    },

    // Question Selection Methods
    toggleAllQuestions() {
      const displayableIds = this.displayableQuestions.map(q => q.id);
      if (this.isAllQuestionsSelected) {
        // Remove all displayable questions from selection
        this.form.selectedQuestionIds = this.form.selectedQuestionIds.filter(id => !displayableIds.includes(id));
      } else {
        // Add all displayable questions to selection
        const currentIds = new Set(this.form.selectedQuestionIds);
        displayableIds.forEach(id => currentIds.add(id));
        this.form.selectedQuestionIds = Array.from(currentIds);
      }
      // Sync categories based on selected questions
      this.syncCategoriesFromQuestions();
    },

    toggleQuestion(questionId) {
      const index = this.form.selectedQuestionIds.indexOf(questionId);
      if (index > -1) {
        this.form.selectedQuestionIds.splice(index, 1);
      } else {
        this.form.selectedQuestionIds.push(questionId);
      }
      // Sync categories based on selected questions
      this.syncCategoriesFromQuestions();
    },

    removeQuestion(questionId) {
      const index = this.form.selectedQuestionIds.indexOf(questionId);
      if (index > -1) {
        this.form.selectedQuestionIds.splice(index, 1);
      }
      // Sync categories based on selected questions
      this.syncCategoriesFromQuestions();
    },

    clearQuestions() {
      this.form.selectedQuestionIds = [];
      // Clear categories when all questions are cleared
      this.form.selectedCategoryIds = [];
    },

    // Sync categories from selected questions (bidirectional linking)
    syncCategoriesFromQuestions() {
      // Get all unique category IDs from selected questions
      const categoryIdsFromQuestions = new Set();
      this.form.selectedQuestionIds.forEach(qId => {
        const question = this.allQuestions.find(q => q.id === qId);
        if (question && question.category) {
          // Find category ID by name
          const categoryId = this.getCategoryIdByName(question.category);
          if (categoryId) {
            categoryIdsFromQuestions.add(categoryId);
          }
        }
      });
      // Update selected category IDs to match (only categories that exist in filteredCategories)
      const validCategoryIds = this.filteredCategories.map(c => c.id);
      this.form.selectedCategoryIds = Array.from(categoryIdsFromQuestions).filter(id => validCategoryIds.includes(id));
    },

    // Helper Methods for display
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    getQuestionChipText(questionId) {
      const question = this.allQuestions.find(q => q.id === questionId);
      if (!question) return `#${questionId}`;
      const text = `#${questionId} - ${question.question || ''}`;
      return this.truncateText(text, 50);
    },

    loadAllQuestions() {
      if (this.allQuestions.length > 0) return; // Already loaded

      this.loadingQuestions = true;
      const request = {
        Page: 1,
        PerPage: 1000, // Load up to 1000 questions for selection
      };

      this.$call.GetTableValidationMaster(
        request,
        (response) => {
          this.loadingQuestions = false;
          if (response.status === 0 && response.data) {
            this.allQuestions = response.data.data || [];
          }
        },
        (error) => {
          this.loadingQuestions = false;
          console.error('Failed to load questions:', error);
        }
      );
    },

    // CRUD Operations
    editJob(job) {
      this.editingJob = job;
      this.loadAllQuestions();

      // Populate form with job data
      this.form = {
        jobName: job.jobName,
        modelId: job.modelId,
        judgeModelId: job.judgeModelId || job.llmJudgeModel || (this.models.length > 0 ? this.models[0].modelDisplayID : null),
        reliability: job.reliability,
        parallelWorkers: job.parallelWorkers,
        selectionType: 'manual', // Always manual now
        selectedCategoryIds: job.categoryIds || [],  // Now uses categoryIds from API response
        selectedQuestionIds: job.questionIds || [],
        limitPerCategory: job.limitPerCategory,
      };

      // Set validation type based on job's ValidationType field
      // ValidationType values: 'answer', 'intent', 'both'
      // Map to: 'answerOnly' (answer), 'answerIntent' (both or intent)
      if (job.validationType === 'both' || job.validationType === 'intent') {
        this.validationType = 'answerIntent';
      } else {
        this.validationType = 'answerOnly';
      }

      this.createDialog = true;
    },

    saveJob(queueImmediately = true) {
      if (!this.formValid) return;

      this.creatingAction = queueImmediately ? 'queue' : 'save';

      // If in edit mode, use update API (only for pending jobs)
      if (this.isEditMode) {
        this.updateExistingJob(queueImmediately);
      } else {
        // Create new job
        this.createNewJob(queueImmediately);
      }
    },

    createNewJob(queueImmediately) {
      const request = {
        JobName: this.form.jobName,
        ModelId: this.form.modelId,
        JudgeModelId: this.form.judgeModelId,
        ParallelWorkers: this.form.parallelWorkers,
        Reliability: this.form.reliability,
        QuestionIds: this.form.selectedQuestionIds,
        CategoryIds: this.form.selectedCategoryIds,  // Changed from Categories to CategoryIds
        ValidationType: this.validationType === 'answerIntent' ? 'both' : 'answer', // Map to V2 format
        QueueImmediately: queueImmediately,
      };

      this.$call.CreateValidationJob(
        request,
        (response) => {
          this.creatingAction = null;
          if (response.status === 0) {
            const actionText = this.isEditMode ? 'Updated' : 'Created';
            this.$swal.fire({
              icon: 'success',
              title: `Job ${actionText}`,
              text: queueImmediately
                ? `"${this.form.jobName}" has been queued`
                : `"${this.form.jobName}" has been ${actionText.toLowerCase()} (pending)`,
              timer: 2000,
              showConfirmButton: false,
            });
            this.closeCreateDialog();
            this.loadJobs();
          } else {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.message || 'Failed to create job',
            });
          }
        },
        (error) => {
          this.creatingAction = null;
          console.error('Failed to create job:', error);
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to create job',
          });
        }
      );
    },

    updateExistingJob(queueImmediately) {
      // Build update request (only for pending jobs)
      const request = {
        JobName: this.form.jobName,
        ModelId: this.form.modelId,
        JudgeModelId: this.form.judgeModelId,
        ParallelWorkers: this.form.parallelWorkers,
        Reliability: this.form.reliability,
        QuestionIds: this.form.selectedQuestionIds,
        CategoryIds: this.form.selectedCategoryIds,  // Changed from Categories to CategoryIds
        ValidationType: this.validationType === 'answerIntent' ? 'both' : 'answer',
      };

      this.$call.UpdateValidationJob(
        this.editingJob.id,
        request,
        (response) => {
          if (response.status === 0) {
            // If queueImmediately is true, queue the job after update
            if (queueImmediately) {
              this.$call.QueueValidationJob(
                this.editingJob.id,
                (queueResponse) => {
                  this.creatingAction = null;
                  if (queueResponse.status === 0) {
                    this.$swal.fire({
                      icon: 'success',
                      title: 'Job Updated & Queued',
                      text: `"${this.form.jobName}" has been updated and queued`,
                      timer: 2000,
                      showConfirmButton: false,
                    });
                  } else {
                    this.$swal.fire({
                      icon: 'warning',
                      title: 'Job Updated',
                      text: `Job updated but failed to queue: ${queueResponse.message}`,
                    });
                  }
                  this.closeCreateDialog();
                  this.loadJobs();
                },
                (error) => {
                  this.creatingAction = null;
                  console.error('Failed to queue job after update:', error);
                  this.$swal.fire({
                    icon: 'warning',
                    title: 'Job Updated',
                    text: 'Job updated but failed to queue',
                  });
                  this.closeCreateDialog();
                  this.loadJobs();
                }
              );
            } else {
              this.creatingAction = null;
              this.$swal.fire({
                icon: 'success',
                title: 'Job Updated',
                text: `"${this.form.jobName}" has been updated (pending)`,
                timer: 2000,
                showConfirmButton: false,
              });
              this.closeCreateDialog();
              this.loadJobs();
            }
          } else {
            this.creatingAction = null;
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.message || 'Failed to update job',
            });
          }
        },
        (error) => {
          this.creatingAction = null;
          console.error('Failed to update job:', error);
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to update job',
          });
        }
      );
    },

    viewJob(job) {
      this.selectedJob = job;
      this.loadJobRunHistory(job.id);
      this.viewDialog = true;
    },

    rerunFromDialog() {
      const job = this.selectedJob;
      this.viewDialog = false;
      this.rerunJob(job);
    },

    cancelJob(job) {
      this.$swal
        .fire({
          title: 'Cancel Job?',
          text: `Are you sure you want to cancel "${job.jobName}"?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, cancel it',
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$call.CancelValidationJob(
              job.id,
              (response) => {
                if (response.status === 0) {
                  this.$swal.fire({
                    icon: 'info',
                    title: 'Job Cancelled',
                    text: 'Job has been cancelled',
                    timer: 1500,
                    showConfirmButton: false,
                  });
                  this.loadJobs();
                } else {
                  this.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to cancel job',
                  });
                }
              },
              (error) => {
                console.error('Failed to cancel job:', error);
                this.$swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: 'Failed to cancel job',
                });
              }
            );
          }
        });
    },

    queueJob(job) {
      this.$swal
        .fire({
          title: 'Queue Job?',
          text: `Queue "${job.jobName}" for processing?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#4caf50',
          cancelButtonColor: '#9e9e9e',
          confirmButtonText: 'Yes, queue it',
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$call.QueueValidationJob(
              job.id,
              (response) => {
                if (response.status === 0) {
                  this.$swal.fire({
                    icon: 'success',
                    title: 'Job Queued',
                    text: 'Job has been added to the queue',
                    timer: 1500,
                    showConfirmButton: false,
                  });
                  this.loadJobs();
                } else {
                  this.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to queue job',
                  });
                }
              },
              (error) => {
                console.error('Failed to queue job:', error);
                this.$swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: 'Failed to queue job',
                });
              }
            );
          }
        });
    },

    confirmDelete(job) {
      this.$swal
        .fire({
          title: 'Delete Job?',
          text: `Are you sure you want to delete "${job.jobName}"?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, delete it',
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$call.DeleteValidationJob(
              job.id,
              (response) => {
                if (response.status === 0) {
                  this.$swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    text: 'Job has been deleted',
                    timer: 1500,
                    showConfirmButton: false,
                  });
                  this.loadJobs();
                } else {
                  this.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to delete job',
                  });
                }
              },
              (error) => {
                console.error('Failed to delete job:', error);
                this.$swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: 'Failed to delete job',
                });
              }
            );
          }
        });
    },

    rerunJob(job) {
      this.$swal
        .fire({
          title: 'Rerun Job?',
          text: `Create a new job with the same configuration as "${job.jobName}"?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#1976d2',
          cancelButtonColor: '#9e9e9e',
          confirmButtonText: 'Yes, rerun it',
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$call.RerunValidationJob(
              job.id,
              (response) => {
                if (response.status === 0) {
                  this.$swal.fire({
                    icon: 'success',
                    title: 'Job Queued',
                    text: `Rerun job has been queued`,
                    timer: 2000,
                    showConfirmButton: false,
                  });
                  this.loadJobs();
                } else {
                  this.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to rerun job',
                  });
                }
              },
              (error) => {
                console.error('Failed to rerun job:', error);
                this.$swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: 'Failed to rerun job',
                });
              }
            );
          }
        });
    },
  },
};
</script>

<style scoped>
.v-data-table {
  font-size: 14px;
}

/* Run History Table */
.run-history-table {
  font-size: 13px;
}

.run-history-table th {
  font-weight: 600 !important;
  color: #666 !important;
  background-color: #f5f5f5 !important;
}

.run-history-table td {
  padding: 8px 12px !important;
}

.run-history-table .current-run {
  background-color: #e3f2fd !important;
}

.run-history-table .current-run td {
  font-weight: 500;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
